import { http } from "./http";
import type { SpotlightFeed, SpotlightFeedPoll, SpotlightSettings, SpotlightSettingsPatch } from "../types/spotlight";

// Request Spotlight: the talent's screen-link settings (signed in), and the
// public display feed the screen link polls (no sign-in, display-only).
export const spotlightApi = {
  settings: () => http.get<SpotlightSettings>("/users/me/spotlight"),
  /** Switch on/off, Style, Fun icons. The first switch-on makes the screen link. */
  update: (patch: SpotlightSettingsPatch) => http.patch<SpotlightSettings>("/users/me/spotlight", patch),
  /** A new screen link; the old one stops working at once. */
  resetLink: () => http.post<SpotlightSettings>("/users/me/spotlight/reset-link"),
  /**
   * One poll of the screen's feed. With the last ETag the server may answer
   * 304 (nothing changed). A 404 (unknown or reset code) and network errors
   * reject, as axios errors do everywhere else.
   */
  feed: async (code: string, etag?: string | null, signal?: AbortSignal): Promise<SpotlightFeedPoll> => {
    const res = await http.getRaw<SpotlightFeed>(`/spotlight/${encodeURIComponent(code)}`, {
      headers: etag ? { "If-None-Match": etag } : undefined,
      validateStatus: (s) => s === 200 || s === 304,
      timeout: 10_000,
      signal,
    });
    if (res.status === 304) return { status: "same" };
    const feed = res.data;
    // The server's ETag is the body's version, quoted ("<v>"); sent back as
    // is, whatever a proxy did to the header (or whether it could be read).
    const header = res.headers?.etag;
    const etagOut = feed?.v ? `"${feed.v}"` : typeof header === "string" && header ? header : null;
    return { status: "fresh", feed, etag: etagOut };
  },
};

/** A screen-link code as the server makes them (12 base62 characters); anything far off is not worth asking about. */
export const isSpotlightCode = (code: string | null | undefined): code is string => !!code && /^[A-Za-z0-9]{6,64}$/.test(code);
